import CoinIcon from "../../../assets/icons/CoinIcon";
import React from "react";

type CheckInStep = {
  day: number;
  coins: number;
  status: string;
};

type EarnCoinsCheckinCardProps = {
  isLoading: boolean;
  claimedCount: number;
  getDayLabel: (count: number) => string;
  checkInClaimed: boolean;
  checkInEligible: boolean;
  checkInUpdating: boolean;
  checkInSteps: CheckInStep[];
  activeIndex: number;
  progressPercent: number;
  animateSuccess: boolean;
  onClaim: () => void;
  resolveLabel: (key: string, fallback: string) => string;
  t: (key: string) => string;
};

const EarnCoinsCheckinCard = ({
  isLoading,
  claimedCount,
  getDayLabel,
  checkInClaimed,
  checkInEligible,
  checkInUpdating,
  checkInSteps,
  activeIndex,
  progressPercent,
  animateSuccess,
  onClaim,
  resolveLabel,
  t,
}: EarnCoinsCheckinCardProps) => {
  const latestDoneIndex = [...checkInSteps]
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => step.status === "done")
    .map(({ index }) => index)
    .pop();

  return (
    <div
      className="earn-coins-card earn-coins-checkin"
      data-loading={isLoading}
    >
      <div className="earn-coins-checkin__header">
        <div className="earn-coins-checkin__left">
          <div className="earn-coins-checkin__icon">
            <span
              className="earn-coins-checkin__icon-check"
              aria-hidden="true"
            ></span>
          </div>
          <div>
            <div className="earn-coins-checkin__title">
              <span>{t("label-daily-check-in")}</span>
              <span className="earn-coins-checkin__badge">
                {claimedCount > 0
                  ? `${claimedCount} ${getDayLabel(claimedCount)}`
                  : getDayLabel(1)}
              </span>
            </div>
            <p>{t("label-check-in-daily")}</p>
          </div>
        </div>
        <button
          className={`earn-coins-checkin__claim${
            checkInClaimed ? " earn-coins-checkin__claim--done" : ""
          }`}
          type="button"
          onClick={onClaim}
          disabled={!checkInEligible || checkInUpdating || checkInClaimed}
        >
          {checkInClaimed
            ? resolveLabel("label-claimed", "Claimed")
            : resolveLabel("label-claim", "Claim")}
          {!checkInClaimed && activeIndex >= 0 && checkInSteps[activeIndex] && (
            <>
              {" "}
              {checkInSteps[activeIndex].coins}
              <CoinIcon
                className="gi-coin-icon gi-coin-small"
                aria-hidden="true"
              />
            </>
          )}
        </button>
      </div>
      <div
        className={`checkin-steps${animateSuccess ? " checkin-steps--animate-success" : ""}`}
      >
        <div className="checkin-steps__track">
          <div
            className={`checkin-steps__track-fill${
              animateSuccess ? " checkin-steps__track-fill--animate" : ""
            }`}
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
        <div className="checkin-steps__items">
          {checkInSteps.map((step, index) => (
            <div
              key={step.day}
              className={`checkin-steps__item checkin-steps__item--${step.status}${
                latestDoneIndex === index ? " checkin-steps__item--latest-done" : ""
              }${
                animateSuccess && latestDoneIndex === index
                  ? " checkin-steps__item--animate"
                  : ""
              }`}
            >
              <div className="checkin-steps__node">
                {step.status === "done" ? (
                  <span className="checkin-steps__check" />
                ) : (
                  step.day
                )}
              </div>
              <div className="checkin-steps__coin">
                <CoinIcon
                  className="gi-coin-icon gi-coin-small"
                  aria-hidden="true"
                />
                <span>{step.coins}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EarnCoinsCheckinCard;
